import type { ReactNode } from 'react';
import { Suspense, lazy } from 'react';
import type { Session } from '@supabase/supabase-js';
import { Outlet, Routes, Route, Navigate } from 'react-router-dom';
import { AlertOctagon } from 'lucide-react';

import {
  LoginScreen,
  SignupScreen,
  ForgotScreen,
  ResetScreen,
  TwoFAScreen,
  VerifyPendingScreen,
  VerifyEmailScreen,
} from '../modules/auth';
import { WelcomeWizard } from '../modules/onboarding';
import { useSession } from './useSession';
import { useTeamMember } from './useTeamMember';
import { Shell } from './Shell';
import { CommandCenter } from '../modules/home';
import {
  SettingsLayout,
  ProfilePage,
  OrganizationPage,
  SecurityPage,
  NotificationsPage,
  LocalePage,
  AuditLogPage,
  DataExportPage,
  IntegrationsHubPage,
  CatalogServicesPage,
  CatalogPipelinePage,
  CatalogSourcesPage,
  CatalogTagsPage,
  CatalogFieldsPage,
  CatalogTemplatesPage,
  CatalogInvoicePage,
  CatalogTeamPage,
} from '../modules/settings';
import { CompaniesPage } from '../modules/crm/companies';
import { LeadsPage, LeadProfile } from '../modules/crm/leads';
import { CustomerProfile } from '../modules/customers';
import {
  ConversationsPage,
  ConversationProfile,
  BotAnalyticsPage,
  BlockedPhonesPage,
  MessageTemplatesPage,
  ScoringRulesPage,
} from '../modules/bot';
import { ProjectsPage, ProjectWorkspace } from '../modules/delivery/projects';
import { TasksPage } from '../modules/delivery/tasks';
import { QuotesPage, QuoteShareView } from '../modules/quotes';
import { FocusPage } from '../modules/focus';
import { HoursPage } from '../modules/hours';
import { InboxPage } from '../modules/inbox';
import { FinancePage } from '../modules/finance';
import { MarketingPage } from '../modules/marketing';
import { DocumentsPage } from '../modules/documents';
import { AnalyticsPage, AttributionPage } from '../modules/analytics';
import { BookingPage } from '../modules/booking';
import { SkillsPage, SkillEditor } from '../modules/skills';
import { GovernancePage } from '../modules/governance';
import { ForbiddenPage, NotFoundPage, StateButton, StateLink, StatePage } from './state-pages';
import { BootScreen } from './BootScreen';

/* The academy carries its whole curriculum as compiled TypeScript, so eager
   imports would put every lesson in the bundle that loads on the login screen.
   These three are the only lazy routes in the app; the Suspense boundary sits
   on the parent, which is enough — the nested routes render inside its Outlet. */
const AcademyLayout = lazy(() =>
  import('../modules/academy').then((m) => ({ default: m.AcademyLayout })),
);
const AcademyHome = lazy(() =>
  import('../modules/academy').then((m) => ({ default: m.AcademyHome })),
);
const LessonPage = lazy(() => import('../modules/academy').then((m) => ({ default: m.LessonPage })));
/* The studio derives its catalogue from the academy content, so it pulls the
   same chunk in. Lazy for the same reason. */
const PromptsLayout = lazy(() =>
  import('../modules/prompts').then((m) => ({ default: m.PromptsLayout })),
);
const PromptStudio = lazy(() =>
  import('../modules/prompts').then((m) => ({ default: m.PromptStudio })),
);
const PromptBuilder = lazy(() =>
  import('../modules/prompts').then((m) => ({ default: m.PromptBuilder })),
);

function RequireAuth({ children }: { children: (session: Session) => ReactNode }) {
  const state = useSession();
  if (state.status === 'loading') return <BootScreen />;
  if (state.status === 'signed-out') return <Navigate to="/auth/login" replace />;
  return <>{children(state.session)}</>;
}

function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const state = useSession();
  if (state.status === 'loading') return <BootScreen />;
  if (state.status === 'signed-in') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ShellRoute() {
  return (
    <RequireAuth>
      {(session) => <ShellRouteInner session={session} />}
    </RequireAuth>
  );
}

function ShellRouteInner({ session }: { session: Session }) {
  const { data, isLoading, error, refetch } = useTeamMember(session);
  if (isLoading) return <BootScreen label="טוען את הארגון…" />;
  // A failed membership lookup is NOT the same thing as "this user has no
  // organization". Treating the two alike is what produced the sign-in dead
  // end: any RLS/network/5xx error here redirected to /welcome, the wizard
  // called bootstrap_org, that replied 409 already_bootstrapped, and the wizard
  // could only show a generic "try again" — leaving the user parked on the
  // welcome screen with no route forward and no idea why. Surface the real
  // failure and let them retry instead.
  if (error) return <MembershipError error={error} onRetry={() => { void refetch(); }} />;
  if (!data) return <Navigate to="/welcome" replace />;
  return <Shell session={session} />;
}

function WelcomeGate() {
  return (
    <RequireAuth>
      {(session) => <WelcomeGateInner session={session} />}
    </RequireAuth>
  );
}

function WelcomeGateInner({ session }: { session: Session }) {
  const { data, isLoading, error, refetch } = useTeamMember(session);
  if (isLoading) return <BootScreen label="טוען את הארגון…" />;
  // Same reasoning as ShellRouteInner: without this branch a lookup failure
  // renders the wizard to a user who already has an organization.
  if (error) return <MembershipError error={error} onRetry={() => { void refetch(); }} />;
  if (data) return <Navigate to="/" replace />;
  return <WelcomeWizard session={session} />;
}

function MembershipError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const detail = error instanceof Error ? error.message : String(error);
  return (
    <StatePage
      code="500"
      icon={AlertOctagon}
      tone="danger"
      title="לא הצלחנו לטעון את הארגון שלך"
      body="ההתחברות הצליחה, אבל שליפת ההרשאות נכשלה. זו כמעט תמיד תקלה זמנית — נסה שוב."
      details={detail}
      primary={
        <StateButton variant="primary" onClick={onRetry}>
          נסה שוב
        </StateButton>
      }
      secondary={<StateLink variant="secondary" to="/auth/login">חזור להתחברות</StateLink>}
    />
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<ShellRoute />}>
        <Route index element={<CommandCenter />} />

        {/* Primary routes — Yoav's actual workflow */}
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadProfile />} />
        <Route path="customers" element={<CompaniesPage />} />
        <Route path="customers/:id" element={<CustomerProfile />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectWorkspace />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="focus" element={<FocusPage />} />
        <Route path="time" element={<HoursPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="finance" element={<FinancePage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="docs" element={<DocumentsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="analytics/attribution" element={<AttributionPage />} />
        <Route path="booking" element={<BookingPage />} />
        <Route path="governance" element={<GovernancePage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="skills/:slug" element={<SkillEditor />} />

        <Route
          path="prompts"
          element={
            <Suspense fallback={<BootScreen label="טוען את הסטודיו…" />}>
              <PromptsLayout />
            </Suspense>
          }
        >
          <Route index element={<PromptStudio />} />
          <Route path="build" element={<PromptBuilder />} />
        </Route>

        {/* Academy — internal knowledge base built on Anthropic's methodology. */}
        <Route
          path="academy"
          element={
            <Suspense fallback={<BootScreen label="טוען את האקדמיה…" />}>
              <AcademyLayout />
            </Suspense>
          }
        >
          <Route index element={<AcademyHome />} />
          <Route path=":lessonId" element={<LessonPage />} />
        </Route>

        {/* Bot area — replaces Notion-side WhatsApp conversation management */}
        <Route path="bot" element={<Navigate to="/bot/conversations" replace />} />
        <Route path="bot/conversations" element={<ConversationsPage />} />
        <Route path="bot/conversations/:id" element={<ConversationProfile />} />
        <Route path="bot/analytics" element={<BotAnalyticsPage />} />
        <Route path="bot/blocked" element={<BlockedPhonesPage />} />
        <Route path="bot/templates" element={<MessageTemplatesPage />} />
        <Route path="bot/scoring" element={<ScoringRulesPage />} />

        {/* Legacy /crm/* paths — 301 redirects to the new structure.
            React Router doesn't emit HTTP 301 in SPA mode; this is a client-side
            redirect that preserves deep-link compatibility. */}
        <Route path="crm" element={<Navigate to="/leads" replace />} />
        <Route path="crm/leads" element={<Navigate to="/leads" replace />} />
        <Route path="crm/companies" element={<Navigate to="/customers" replace />} />
        <Route path="crm/deals" element={<Navigate to="/projects?status=quote,active" replace />} />
        <Route path="crm/pipeline" element={<Navigate to="/projects" replace />} />
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="locale" element={<LocalePage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="data" element={<DataExportPage />} />
          <Route path="integrations" element={<IntegrationsHubPage />} />
          <Route path="catalogs/services" element={<CatalogServicesPage />} />
          <Route path="catalogs/pipeline" element={<CatalogPipelinePage />} />
          <Route path="catalogs/sources" element={<CatalogSourcesPage />} />
          <Route path="catalogs/tags" element={<CatalogTagsPage />} />
          <Route path="catalogs/custom-fields" element={<CatalogFieldsPage />} />
          <Route path="catalogs/templates" element={<CatalogTemplatesPage />} />
          <Route path="catalogs/invoice-settings" element={<CatalogInvoicePage />} />
          <Route path="catalogs/team" element={<CatalogTeamPage />} />
        </Route>
        <Route path="403" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="/welcome" element={<WelcomeGate />} />

      {/* Public quote share view — no auth, no shell. UUID v4 share token in URL. */}
      <Route path="/q/:id" element={<QuoteShareView />} />

      <Route element={<AuthLayout />}>
        <Route
          path="/auth/login"
          element={
            <RedirectIfSignedIn>
              <LoginScreen />
            </RedirectIfSignedIn>
          }
        />
        <Route
          path="/auth/signup"
          element={
            <RedirectIfSignedIn>
              <SignupScreen />
            </RedirectIfSignedIn>
          }
        />
        <Route path="/auth/forgot" element={<ForgotScreen />} />
        <Route path="/auth/reset" element={<ResetScreen />} />
        <Route path="/auth/2fa" element={<TwoFAScreen />} />
        <Route path="/auth/verify-pending" element={<VerifyPendingScreen />} />
        <Route path="/auth/verify" element={<VerifyEmailScreen />} />
      </Route>
    </Routes>
  );
}

function AuthLayout() {
  return <Outlet />;
}
